import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import apiService, { OrderStatistics, Order } from '../../services/api';

const OrdersWrapper = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const OrdersHeader = styled.div`
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-border);

  h2 {
    margin: 0 0 var(--spacing-4) 0;
    color: var(--color-text);
    font-size: var(--font-size-xl);
    font-weight: 600;
  }
`;

const ControlsWrapper = styled.div`
  display: flex;
  gap: var(--spacing-4);
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: var(--spacing-4);
`;

const SearchWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const SearchIcon = styled.div<{ isSearching: boolean }>`
  position: absolute;
  right: var(--spacing-3);
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: var(--font-size-sm);
  pointer-events: none;
  transition: color 0.2s ease;
  
  ${({ isSearching }) => isSearching && `
    color: var(--color-secondary);
  `}
`;

const SearchInput = styled.input`
  padding: var(--spacing-3) var(--spacing-4);
  padding-right: var(--spacing-8);
  border: 2px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  width: 400px;
  background: #fafafa;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: #9ca3af;
    font-style: italic;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    background: white;
    box-shadow: 0 0 0 3px rgba(139, 69, 19, 0.1);
    transform: translateY(-1px);
  }
  
  &:hover {
    border-color: #d1d5db;
    background: white;
  }
`;

const Select = styled.select`
  padding: var(--spacing-3) var(--spacing-4);
  border: 2px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  background: #fafafa;
  cursor: pointer;
  min-width: 180px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    background: white;
    box-shadow: 0 0 0 3px rgba(139, 69, 19, 0.1);
    transform: translateY(-1px);
  }
  
  &:hover {
    border-color: #d1d5db;
    background: white;
  }
`;

const SortButton = styled.button<{ active: boolean; direction: 'ASC' | 'DESC' }>`
  background: none;
  border: none;
  color: var(--color-text);
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: var(--border-radius);
  
  &:hover {
    background: #f3f4f6;
  }
  
  ${({ active }) => active && `
    color: var(--color-secondary);
  `}
  
  &::after {
    content: '${({ active, direction }) => active ? (direction === 'ASC' ? '↑' : '↓') : '↕'}';
    opacity: ${({ active }) => active ? 1 : 0.5};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-4);
  padding: var(--spacing-6);
  background: #f8f9fa;
`;

const StatCard = styled.div`
  background: white;
  padding: var(--spacing-4);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-sm);
  text-align: center;

  .stat-number {
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-secondary);
    margin-bottom: var(--spacing-2);
  }

  .stat-label {
    color: var(--color-text-light);
    font-size: var(--font-size-sm);
    font-weight: 500;
  }

  &.urgent .stat-number {
    color: #dc2626;
  }

  &.success .stat-number {
    color: #059669;
  }
`;

const OrderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    text-align: left;
    padding: var(--spacing-3) var(--spacing-4);
    border-bottom: 1px solid var(--color-border);
  }
  
  th {
    background: #f8f9fa;
    font-weight: 600;
    color: var(--color-text);
    font-size: var(--font-size-sm);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  tbody tr:hover {
    background: #f8f9fa;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: var(--font-size-xs);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${({ status }) => {
    switch (status) {
      case 'pending':
        return 'background: #fef3c7; color: #92400e;';
      case 'sent':
        return 'background: #d1fae5; color: #065f46;';
      case 'cancelled':
        return 'background: #fee2e2; color: #991b1b;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const StatusSelect = styled.select<{ status: string }>`
  padding: 6px 8px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  font-size: var(--font-size-xs);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  min-width: 90px;
  
  ${({ status }) => {
    switch (status) {
      case 'pending':
        return 'background: #fef3c7; color: #92400e; border-color: #f59e0b;';
      case 'sent':
        return 'background: #d1fae5; color: #065f46; border-color: #10b981;';
      case 'cancelled':
        return 'background: #fee2e2; color: #991b1b; border-color: #ef4444;';
      default:
        return 'background: #f3f4f6; color: #374151; border-color: #d1d5db;';
    }
  }}
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.1);
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: var(--spacing-8);
  color: var(--color-text-light);
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: var(--spacing-8);
  color: var(--color-error);
`;

const DeleteButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: var(--border-radius);
  padding: var(--spacing-2) var(--spacing-3);
  font-size: var(--font-size-xs);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover:not(:disabled) {
    background-color: #c82333;
  }
  
  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const ViewButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: var(--border-radius);
  padding: var(--spacing-2) var(--spacing-3);
  font-size: var(--font-size-xs);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: var(--spacing-2);
  
  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
  
  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-4);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: between;
  align-items: center;
  
  h3 {
    margin: 0;
    color: var(--color-text);
    font-size: var(--font-size-xl);
    font-weight: 600;
  }
`;

const ModalBody = styled.div`
  padding: var(--spacing-6);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: var(--font-size-xl);
  cursor: pointer;
  color: var(--color-text-light);
  padding: 4px;
  margin-left: auto;
  
  &:hover {
    color: var(--color-text);
  }
`;

const OrderDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);
`;

const DetailCard = styled.div`
  background: #f8f9fa;
  padding: var(--spacing-4);
  border-radius: var(--border-radius);
  
  h4 {
    margin: 0 0 var(--spacing-3) 0;
    color: var(--color-text);
    font-size: var(--font-size-base);
    font-weight: 600;
  }
  
  p {
    margin: 0 0 var(--spacing-2) 0;
    color: var(--color-text-light);
    font-size: var(--font-size-sm);
    
    strong {
      color: var(--color-text);
    }
  }
`;

const OrderItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: var(--spacing-4);
  
  th, td {
    text-align: left;
    padding: var(--spacing-3);
    border-bottom: 1px solid var(--color-border);
  }
  
  th {
    background: #f8f9fa;
    font-weight: 600;
    color: var(--color-text);
    font-size: var(--font-size-sm);
  }
  
  td {
    color: var(--color-text-light);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-8);
  color: var(--color-text-light);
`;

const AdminOrders: React.FC = () => {
  const [statistics, setStatistics] = useState<OrderStatistics | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>(''); // What user types
  const [debouncedSearch, setDebouncedSearch] = useState<string>(''); // Actual search term used
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [deletingOrder, setDeletingOrder] = useState<number | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300); // 300ms delay - quick enough to feel responsive, slow enough to not interrupt typing

    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const [statsData, ordersData] = await Promise.all([
        apiService.getOrderStatistics(),
        apiService.getOrders({ 
          limit: 50, 
          search: debouncedSearch || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          status: statusFilter || undefined
        })
      ]);
      
      setStatistics(statsData);
      setOrders(ordersData);
    } catch (err) {
      console.error('Error fetching order data:', err);
      setError('Kunde inte ladda orderdata. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, sortBy, sortOrder, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
  };

  const handleStatusChange = async (orderId: number, newStatus: string) => {
    const originalOrder = orders.find(o => o.id === orderId);
    if (!originalOrder) return;

    try {
      const response = await apiService.updateOrderStatus(
        orderId, 
        newStatus, 
        newStatus === 'sent' ? new Date().toISOString().slice(0, 19).replace('T', ' ') : undefined
      );
      
      // Update the order in the local state
      setOrders(orders.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              status: newStatus as any, 
              shipped_at: newStatus === 'sent' ? new Date().toISOString() : order.shipped_at 
            }
          : order
      ));
      
      // Refetch statistics
      const statsData = await apiService.getOrderStatistics();
      setStatistics(statsData);
      
    } catch (err: any) {
      console.error('Error updating order status:', err);
      
      // Revert the select to original status
      const selectElement = document.querySelector(`select[data-order-id="${orderId}"]`) as HTMLSelectElement;
      if (selectElement) {
        selectElement.value = originalOrder.status;
      }
      
      alert(`Kunde inte uppdatera orderstatus. Fel: ${err.message || 'Okänt fel'}`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(amount);
  };

  const handleDeleteOrder = async (order: Order) => {
    const confirmed = window.confirm(
      `Är du säker på att du vill ta bort ordern ${order.order_number} för ${order.first_name} ${order.last_name}?\n\nDetta kan inte ångras!`
    );
    
    if (!confirmed) return;

    setDeletingOrder(order.id);
    try {
      await apiService.deleteOrder(order.id);
      
      // Remove order from local state
      setOrders(orders.filter(o => o.id !== order.id));
      
      // Refresh statistics
      const statsData = await apiService.getOrderStatistics();
      setStatistics(statsData);
      
      setError('');
    } catch (err: any) {
      setError(`Kunde inte ta bort order: ${err.message}`);
    } finally {
      setDeletingOrder(null);
    }
  };

  const handleViewOrder = async (order: Order) => {
    setLoadingOrderDetails(true);
    setShowOrderDetails(true);
    
    try {
      const orderDetails = await apiService.getOrderDetails(order.id);
      setSelectedOrder(orderDetails);
      setError('');
    } catch (err: any) {
      setError(`Kunde inte ladda orderdetaljer: ${err.message}`);
      setShowOrderDetails(false);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  if (loading) {
    return (
      <OrdersWrapper>
        <LoadingMessage>Laddar orderdata...</LoadingMessage>
      </OrdersWrapper>
    );
  }

  if (error) {
    return (
      <OrdersWrapper>
        <ErrorMessage>{error}</ErrorMessage>
      </OrdersWrapper>
    );
  }

  return (
    <OrdersWrapper>
      <OrdersHeader>
        <h2>📦 Ordrar</h2>
        
        <ControlsWrapper>
          <SearchWrapper>
            <SearchInput
              type="text"
              placeholder="Sök ordrar (ordernummer, namn, email...)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <SearchIcon isSearching={searchInput !== debouncedSearch || Boolean(debouncedSearch && loading)}>
              {searchInput !== debouncedSearch ? '⏳' : '🔍'}
            </SearchIcon>
          </SearchWrapper>
          
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Alla statusar</option>
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </ControlsWrapper>
      </OrdersHeader>

      {statistics && (
        <StatsGrid>
          <StatCard className="urgent">
            <div className="stat-number">{statistics.not_shipped}</div>
            <div className="stat-label">Ordrar som inte ännu är skickade</div>
          </StatCard>
          
          <StatCard>
            <div className="stat-number">{statistics.shipped_today}</div>
            <div className="stat-label">Ordrar skickade idag</div>
          </StatCard>
          
          <StatCard className="success">
            <div className="stat-number">{statistics.shipped_this_week}</div>
            <div className="stat-label">Ordrar skickade senaste veckan</div>
          </StatCard>
          
          <StatCard className="success">
            <div className="stat-number">{statistics.shipped_this_month}</div>
            <div className="stat-label">Ordrar skickade den här månaden</div>
          </StatCard>
        </StatsGrid>
      )}

      <div style={{ padding: '0 var(--spacing-6) var(--spacing-6)' }}>
        <h3 style={{ marginBottom: 'var(--spacing-4)', color: 'var(--color-text)' }}>
          Senaste ordrar
        </h3>
        
        {orders.length > 0 ? (
          <OrderTable>
            <thead>
              <tr>
                <th>Ordernummer</th>
                <th>Kund</th>
                <th>Email</th>
                <th>Leverans</th>
                <th>Status</th>
                <th>
                  <SortButton 
                    active={sortBy === 'total_amount'} 
                    direction={sortOrder}
                    onClick={() => handleSort('total_amount')}
                  >
                    Belopp
                  </SortButton>
                </th>
                <th>Nettovinst</th>
                <th>
                  <SortButton 
                    active={sortBy === 'created_at'} 
                    direction={sortOrder}
                    onClick={() => handleSort('created_at')}
                  >
                    Skapad
                  </SortButton>
                </th>
                <th>Skickad</th>
                <th>Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td style={{ fontWeight: '500' }}>{order.order_number}</td>
                  <td>{order.first_name} {order.last_name}</td>
                  <td>{order.email}</td>
                  <td>
                    {order.shipping_address === 'Hämtas ut i butik' || order.notes === 'Hämtas ut i butik' ? (
                      <span style={{ color: '#059669', fontWeight: '500' }}>🏪 Hämtas ut</span>
                    ) : (
                      <span style={{ color: '#374151' }}>📦 Frakt</span>
                    )}
                  </td>
                  <td>
                    <StatusSelect
                      status={order.status}
                      value={order.status}
                      data-order-id={order.id}
                      onChange={(e) => handleStatusChange(order.id, e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="sent">Sent</option>
                      <option value="cancelled">Cancelled</option>
                    </StatusSelect>
                  </td>
                  <td style={{ fontWeight: '500' }}>
                    {formatCurrency(order.total_amount)}
                  </td>
                  <td style={{ fontWeight: '500', color: (order.net_profit ?? 0) < 0 ? '#dc2626' : 'var(--color-secondary)' }}>
                    {formatCurrency(order.net_profit ?? 0)}
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>
                    {order.shipped_at ? formatDate(order.shipped_at) : '-'}
                  </td>
                  <td>
                    <ActionButtons>
                      <ViewButton
                        onClick={() => handleViewOrder(order)}
                        disabled={loadingOrderDetails}
                      >
                        👁️ Visa order
                      </ViewButton>
                      <DeleteButton
                        onClick={() => handleDeleteOrder(order)}
                        disabled={deletingOrder === order.id}
                      >
                        {deletingOrder === order.id ? '⏳' : '🗑️'} Ta bort
                      </DeleteButton>
                    </ActionButtons>
                  </td>
                </tr>
              ))}
            </tbody>
          </OrderTable>
        ) : (
          <div style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-text-light)' }}>
            Inga ordrar hittades.
          </div>
        )}
      </div>

      {showOrderDetails && (
        <Modal onClick={(e) => e.target === e.currentTarget && setShowOrderDetails(false)}>
          <ModalContent>
            <ModalHeader>
              <h3>📦 Orderdetaljer</h3>
              <CloseButton onClick={() => setShowOrderDetails(false)}>✕</CloseButton>
            </ModalHeader>
            <ModalBody>
              {loadingOrderDetails ? (
                <LoadingSpinner>
                  <div>⏳ Laddar orderdetaljer...</div>
                </LoadingSpinner>
              ) : selectedOrder ? (
                <>
                  <OrderDetailsGrid>
                    <DetailCard>
                      <h4>📋 Grundläggande information</h4>
                      <p><strong>Ordernummer:</strong> {selectedOrder.order_number}</p>
                      <p><strong>Status:</strong> {selectedOrder.status}</p>
                      <p><strong>Betalningsstatus:</strong> {selectedOrder.payment_status}</p>
                      <p><strong>Betalningsmetod:</strong> {selectedOrder.payment_method || 'Ej specificerad'}</p>
                      <p><strong>Skapad:</strong> {formatDate(selectedOrder.created_at)}</p>
                      {selectedOrder.shipped_at && (
                        <p><strong>Skickad:</strong> {formatDate(selectedOrder.shipped_at)}</p>
                      )}
                    </DetailCard>

                    <DetailCard>
                      <h4>👤 Kunduppgifter</h4>
                      <p><strong>Namn:</strong> {selectedOrder.first_name} {selectedOrder.last_name}</p>
                      <p><strong>E-post:</strong> {selectedOrder.email}</p>
                      {selectedOrder.phone && (
                        <p><strong>Telefon:</strong> {selectedOrder.phone}</p>
                      )}
                    </DetailCard>

                    <DetailCard>
                      <h4>{selectedOrder.shipping_address === 'Hämtas ut i butik' ? '🏪 Leverans' : '🏠 Adresser'}</h4>
                      {selectedOrder.shipping_address === 'Hämtas ut i butik' ? (
                        <>
                          <p><strong>Leveranstyp:</strong> Hämtas ut i butik</p>
                          <p><strong>Fakturaadress:</strong><br />{selectedOrder.billing_address}</p>
                        </>
                      ) : (
                        <>
                          <p><strong>Fakturaadress:</strong><br />{selectedOrder.billing_address}</p>
                          {selectedOrder.shipping_address && (
                            <p><strong>Leveransadress:</strong><br />{selectedOrder.shipping_address}</p>
                          )}
                        </>
                      )}
                    </DetailCard>

                    <DetailCard>
                      <h4>💰 Belopp</h4>
                      <p><strong>Totalsumma:</strong> {formatCurrency(selectedOrder.total_amount)}</p>
                      <p><strong>{selectedOrder.shipping_address === 'Hämtas ut i butik' ? 'Hämta ut i butik:' : 'Frakt:'}</strong> {formatCurrency(selectedOrder.shipping_cost || 0)}</p>
                      <p><strong>Moms:</strong> {formatCurrency(selectedOrder.tax_amount || 0)}</p>
                      <p><strong>Nettovinst:</strong> {formatCurrency(selectedOrder.net_profit ?? 0)}</p>
                      {selectedOrder.notes && (
                        <p><strong>Anteckningar:</strong> {selectedOrder.notes}</p>
                      )}
                    </DetailCard>
                  </OrderDetailsGrid>

                  {selectedOrder.items && selectedOrder.items.length > 0 && (
                    <div>
                      <h4>🛍️ Beställda produkter</h4>
                      <OrderItemsTable>
                        <thead>
                          <tr>
                            <th>Produkt</th>
                            <th>Antal</th>
                            <th>Pris per st</th>
                            <th>Totalt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.items.map((item: any, index: number) => (
                            <tr key={index}>
                              <td><strong>{item.product_name || item.name || 'Okänd produkt'}</strong></td>
                              <td>{item.quantity}</td>
                              <td>{formatCurrency(item.price)}</td>
                              <td>{formatCurrency(item.quantity * item.price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </OrderItemsTable>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-text-light)' }}>
                  Kunde inte ladda orderdetaljer.
                </div>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </OrdersWrapper>
  );
};

export default AdminOrders;
